import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ProspectStatusBadge from './ProspectStatusBadge';
import { prospectService } from '../../../services/prospectService';
import FollowupModal from './FollowupModal';

const INTERACTION_ICONS = {
  call: 'Phone', email: 'Mail', meeting: 'Users', site_visit: 'MapPin',
  proposal: 'FileText', demo: 'Monitor', follow_up: 'RefreshCw', other: 'MessageSquare'
};

const FOLLOWUP_STATUS_COLORS = {
  scheduled: 'text-blue-600 bg-blue-50',
  completed: 'text-green-600 bg-green-50',
  cancelled: 'text-gray-500 bg-gray-50',
  overdue: 'text-red-600 bg-red-50'
};

const ProspectDetailPanel = ({ prospect, onClose, onEdit, onConvert, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [contacts, setContacts] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [followupModalOpen, setFollowupModalOpen] = useState(false);
  const [editingFollowup, setEditingFollowup] = useState(null);
  const [logInteractionOpen, setLogInteractionOpen] = useState(false);
  const [interactionForm, setInteractionForm] = useState({ interactionType: 'call', subject: '', notes: '', outcome: '' });
  const [savingInteraction, setSavingInteraction] = useState(false);

  useEffect(() => {
    if (prospect?.id) loadDetails();
  }, [prospect?.id]);

  const loadDetails = async () => {
    setLoading(true);
    const [c, i, f] = await Promise.all([
      prospectService?.getContacts(prospect?.id),
      prospectService?.getInteractions(prospect?.id),
      prospectService?.getFollowups(prospect?.id)
    ]);
    setContacts(c?.data || []);
    setInteractions(i?.data || []);
    setFollowups(f?.data || []);
    setLoading(false);
  };

  const handleSaveFollowup = async (data) => {
    let result;
    if (editingFollowup) {
      result = await prospectService?.updateFollowup(editingFollowup?.id, data);
    } else {
      result = await prospectService?.createFollowup({ ...data, prospectId: prospect?.id });
    }
    if (!result?.error) {
      setFollowupModalOpen(false);
      setEditingFollowup(null);
      loadDetails();
      onRefresh?.();
    }
  };

  const handleDeleteFollowup = async (id) => {
    if (!window.confirm('Delete this follow-up?')) return;
    await prospectService?.deleteFollowup(id);
    loadDetails();
  };

  const handleCompleteFollowup = async (followup) => {
    await prospectService?.updateFollowup(followup?.id, { followupStatus: 'completed', completedAt: new Date()?.toISOString() });
    loadDetails();
    onRefresh?.();
  };

  const handleLogInteraction = async (e) => {
    e?.preventDefault();
    if (!interactionForm?.subject?.trim()) return;
    setSavingInteraction(true);
    let result = await prospectService?.createInteraction({ ...interactionForm, prospectId: prospect?.id });
    if (!result?.error) {
      setLogInteractionOpen(false);
      setInteractionForm({ interactionType: 'call', subject: '', notes: '', outcome: '' });
      loadDetails();
      onRefresh?.();
    }
    setSavingInteraction(false);
  };

  const exportToICal = () => {
    const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//ConstructionPro//Prospects//EN'];
    followups?.filter(f => f?.followupStatus === 'scheduled')?.forEach(f => {
      const dt = new Date(f.scheduledAt);
      const fmt = d => d?.toISOString()?.replace(/[-:]/g, '')?.split('.')?.[0] + 'Z';
      lines?.push('BEGIN:VEVENT',
        `UID:followup-${f?.id}@constructionpro`,
        `DTSTART:${fmt(dt)}`,
        `DTEND:${fmt(new Date(dt.getTime() + 3600000))}`,
        `SUMMARY:${f?.title} - ${prospect?.companyName}`,
        `DESCRIPTION:${f?.description || ''}`,
        'END:VEVENT');
    });
    lines?.push('END:VCALENDAR');
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `followups-${prospect?.companyName?.replace(/\s+/g, '-')}.ics`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const addToGoogleCalendar = (followup) => {
    const start = new Date(followup.scheduledAt);
    const end = new Date(start.getTime() + 3600000);
    const fmt = d => d?.toISOString()?.replace(/[-:]/g, '')?.split('.')?.[0] + 'Z';
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(followup?.title + ' - ' + prospect?.companyName)}&dates=${fmt(start)}/${fmt(end)}&details=${encodeURIComponent(followup?.description || '')}`;
    window.open(url, '_blank');
  };

  if (!prospect) return null;

  const primaryContact = contacts?.find(c => c?.isPrimary) || contacts?.[0];
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'contacts', label: `Contacts (${contacts?.length})`, icon: 'Users' },
    { id: 'interactions', label: `Activity (${interactions?.length})`, icon: 'MessageSquare' },
    { id: 'followups', label: `Follow-ups (${followups?.length})`, icon: 'Calendar' },
  ];

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-card w-full max-w-2xl h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-border bg-primary text-primary-foreground">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold truncate">{prospect?.companyName}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <ProspectStatusBadge status={prospect?.status} />
              {prospect?.companyType && (
                <span className="text-xs text-primary-foreground/70 capitalize">{prospect?.companyType?.replace(/_/g, ' ')}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Button size="sm" variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => onEdit(prospect)} iconName="Edit" iconPosition="left">Edit</Button>
            {prospect?.status !== 'won' && (
              <Button size="sm" variant="success" onClick={() => onConvert(prospect)} iconName="UserCheck" iconPosition="left">Convert</Button>
            )}
            <button onClick={onClose} className="text-white/70 hover:text-white ml-1">
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border bg-card">
          {tabs?.map(tab => (
            <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id ? 'border-accent text-accent' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}>
              <Icon name={tab?.icon} size={14} />
              {tab?.label}
            </button>
          ))}
        </div>

        <div className="flex-1 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
            </div>
          ) : (
            <>
              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'Estimated Value', value: prospect?.estimatedValue ? `$${Number(prospect?.estimatedValue)?.toLocaleString()}` : '—', icon: 'DollarSign' },
                      { label: 'Win Probability', value: prospect?.probability ? `${prospect?.probability}%` : '—', icon: 'TrendingUp' },
                      { label: 'Lead Score', value: prospect?.leadScore ?? '—', icon: 'Star' },
                      { label: 'Source', value: prospect?.source?.replace(/_/g, ' ') || '—', icon: 'Target' },
                    ]?.map(item => (
                      <div key={item?.label} className="bg-muted/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Icon name={item?.icon} size={14} />
                          <span className="text-xs">{item?.label}</span>
                        </div>
                        <p className="text-sm font-semibold text-foreground capitalize">{item?.value}</p>
                      </div>
                    ))}
                  </div>

                  {primaryContact && (
                    <div className="border border-border rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Icon name="User" size={14} className="text-accent" /> Primary Contact
                      </h4>
                      <p className="font-medium text-foreground">{primaryContact?.firstName} {primaryContact?.lastName}</p>
                      {primaryContact?.title && <p className="text-sm text-muted-foreground">{primaryContact?.title}</p>}
                      <div className="mt-2 space-y-1">
                        {primaryContact?.email && (
                          <a href={`mailto:${primaryContact?.email}`} className="flex items-center gap-2 text-sm text-accent hover:underline">
                            <Icon name="Mail" size={13} /> {primaryContact?.email}
                          </a>
                        )}
                        {primaryContact?.phone && (
                          <a href={`tel:${primaryContact?.phone}`} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon name="Phone" size={13} /> {primaryContact?.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {prospect?.notes && (
                    <div className="border border-border rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                        <Icon name="FileText" size={14} className="text-accent" /> Notes
                      </h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{prospect?.notes}</p>
                    </div>
                  )}

                  {prospect?.convertedAt && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-green-700">
                        <Icon name="CheckCircle" size={16} />
                        <span className="text-sm font-medium">Converted to Customer on {format(new Date(prospect.convertedAt), 'MMM d, yyyy')}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* CONTACTS TAB */}
              {activeTab === 'contacts' && (
                <div className="space-y-3">
                  {contacts?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="Users" size={32} className="mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No contacts yet</p>
                    </div>
                  ) : contacts?.map(contact => (
                    <div key={contact?.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground">{contact?.firstName} {contact?.lastName}</p>
                            {contact?.isPrimary && (
                              <span className="text-xs bg-accent/20 text-accent px-1.5 py-0.5 rounded">Primary</span>
                            )}
                          </div>
                          {contact?.title && <p className="text-sm text-muted-foreground">{contact?.title}</p>}
                          <div className="mt-1 space-y-0.5">
                            {contact?.email && <p className="text-sm text-muted-foreground">{contact?.email}</p>}
                            {contact?.phone && <p className="text-sm text-muted-foreground">{contact?.phone}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* INTERACTIONS TAB */}
              {activeTab === 'interactions' && (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <Button size="sm" iconName="Plus" iconPosition="left" onClick={() => setLogInteractionOpen(true)}>Log Activity</Button>
                  </div>
                  {logInteractionOpen && (
                    <form onSubmit={handleLogInteraction} className="border border-border rounded-lg p-4 space-y-3 bg-muted/20">
                      <h4 className="text-sm font-semibold">Log New Activity</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <select className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                          value={interactionForm?.interactionType}
                          onChange={e => setInteractionForm(p => ({ ...p, interactionType: e?.target?.value }))}>
                          {[['call','Call'],['email','Email'],['meeting','Meeting'],['site_visit','Site Visit'],['proposal','Proposal'],['demo','Demo'],['other','Other']]?.map(([v,l]) => (
                            <option key={v} value={v}>{l}</option>
                          ))}
                        </select>
                        <input className="h-10 rounded-md border border-input bg-background px-3 text-sm" placeholder="Subject *"
                          value={interactionForm?.subject}
                          onChange={e => setInteractionForm(p => ({ ...p, subject: e?.target?.value }))} />
                      </div>
                      <textarea className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px] resize-y"
                        placeholder="Notes..." value={interactionForm?.notes}
                        onChange={e => setInteractionForm(p => ({ ...p, notes: e?.target?.value }))} />
                      <input className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" placeholder="Outcome"
                        value={interactionForm?.outcome}
                        onChange={e => setInteractionForm(p => ({ ...p, outcome: e?.target?.value }))} />
                      <div className="flex gap-2 justify-end">
                        <Button type="button" size="sm" variant="outline" onClick={() => setLogInteractionOpen(false)}>Cancel</Button>
                        <Button type="submit" size="sm" loading={savingInteraction}>Save</Button>
                      </div>
                    </form>
                  )}
                  {interactions?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="MessageSquare" size={32} className="mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No activity logged yet</p>
                    </div>
                  ) : interactions?.map(interaction => (
                    <div key={interaction?.id} className="flex gap-3 border-b border-border pb-4 last:border-0">
                      <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name={INTERACTION_ICONS?.[interaction?.interactionType] || 'MessageSquare'} size={14} className="text-accent" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground">{interaction?.subject}</p>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {interaction?.interactionDate ? format(new Date(interaction.interactionDate), 'MMM d, yyyy') : ''}
                          </span>
                        </div>
                        {interaction?.notes && <p className="text-sm text-muted-foreground mt-0.5">{interaction?.notes}</p>}
                        {interaction?.outcome && (
                          <p className="text-xs text-accent mt-1">Outcome: {interaction?.outcome}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* FOLLOW-UPS TAB */}
              {activeTab === 'followups' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      <Button size="sm" iconName="Plus" iconPosition="left" onClick={() => { setEditingFollowup(null); setFollowupModalOpen(true); }}>Schedule</Button>
                      {followups?.some(f => f?.followupStatus === 'scheduled') && (
                        <Button size="sm" variant="outline" iconName="Download" iconPosition="left" onClick={exportToICal}>Export iCal</Button>
                      )}
                    </div>
                  </div>
                  {followups?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="Calendar" size={32} className="mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No follow-ups scheduled</p>
                    </div>
                  ) : followups?.map(followup => (
                    <div key={followup?.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-foreground">{followup?.title}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${FOLLOWUP_STATUS_COLORS?.[followup?.followupStatus] || 'text-gray-600 bg-gray-50'}`}>
                              {followup?.followupStatus?.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            <Icon name="Clock" size={11} className="inline mr-1" />
                            {followup?.scheduledAt ? format(new Date(followup.scheduledAt), 'MMM d, yyyy h:mm a') : ''}
                          </p>
                          {followup?.description && <p className="text-sm text-muted-foreground mt-1">{followup?.description}</p>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {followup?.followupStatus === 'scheduled' && (
                            <>
                              <button onClick={() => addToGoogleCalendar(followup)} title="Add to Google Calendar"
                                className="p-1.5 text-muted-foreground hover:text-foreground rounded">
                                <Icon name="ExternalLink" size={14} />
                              </button>
                              <button onClick={() => handleCompleteFollowup(followup)} title="Mark complete"
                                className="p-1.5 text-green-600 hover:text-green-700 rounded">
                                <Icon name="CheckCircle" size={14} />
                              </button>
                            </>
                          )}
                          <button onClick={() => { setEditingFollowup(followup); setFollowupModalOpen(true); }}
                            className="p-1.5 text-muted-foreground hover:text-foreground rounded">
                            <Icon name="Edit" size={14} />
                          </button>
                          <button onClick={() => handleDeleteFollowup(followup?.id)}
                            className="p-1.5 text-muted-foreground hover:text-destructive rounded">
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <FollowupModal
        isOpen={followupModalOpen}
        onClose={() => { setFollowupModalOpen(false); setEditingFollowup(null); }}
        onSave={handleSaveFollowup}
        followup={editingFollowup}
        prospectId={prospect?.id}
      />
    </div>
  );
};

export default ProspectDetailPanel;
